import { useEffect, useRef, useState } from 'react';
import { SelectionInfo } from 'dragonmouse-shared/devigner';
import { Frame } from 'dragonmouse-shared/interfaces';
import ColorInput from 'components/Widget/Input/ColorInput';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { applyStyleProperties } from 'visualEdits';
import { trackVisualEditAction } from 'analytics';
import { cssColorToHex } from 'utils';
import Field from '../Field';

interface ColorEditControlProps {
	frame: Frame;
	selectionInfo: SelectionInfo;
}

// True if a CSS color string represents a transparent (unset) value —
// `transparent` keyword, or any rgba(...) with alpha === 0.
const isTransparent = (css: string): boolean => {
	if (!css || css === 'transparent') return true;
	const m = css.match(/rgba?\(([^)]+)\)/);
	if (!m) return false;
	const parts = m[1].split(',').map((p) => parseFloat(p.trim()));
	return parts.length === 4 && parts[3] === 0;
};

const ColorEditControl = ({ frame, selectionInfo }: ColorEditControlProps) => {
	const elementId = selectionInfo.dataset?.mcElementId ?? selectionInfo.element.id;

	const rawBg = selectionInfo.computedStyles?.backgroundColor ?? 'rgb(255,255,255)';
	const rawText = selectionInfo.computedStyles?.color ?? 'rgb(0,0,0)';
	const initialBg = cssColorToHex(rawBg);
	const initialText = cssColorToHex(rawText);

	const [bgColor, setBgColor] = useState(initialBg);
	const [textColor, setTextColor] = useState(initialText);
	// "Unset" tracks whether the displayed swatch color is real or a placeholder
	// for a transparent value. Cleared on the user's first explicit change.
	const [bgUnset, setBgUnset] = useState(isTransparent(rawBg));
	const [textUnset, setTextUnset] = useState(isTransparent(rawText));
	const debouncedBg = useDebouncedValue(bgColor);
	const debouncedText = useDebouncedValue(textColor);

	// Skip-initial guards so mounting doesn't fire a no-op write to forge.
	const initialBgRef = useRef(initialBg);
	const initialTextRef = useRef(initialText);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		if (debouncedBg === initialBgRef.current) return;
		void commit('background-color', debouncedBg);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedBg]);

	useEffect(() => {
		if (debouncedText === initialTextRef.current) return;
		void commit('color', debouncedText);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedText]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const commit = async (property: string, value: string) => {
		if (!frame.siteId) return;
		trackVisualEditAction('commit_color_edit');
		await applyStyleProperties(frame.id, frame.siteId, elementId, { [property]: value });
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const onBgChange = (color: string) => {
		setBgColor(color);
		setBgUnset(false);
	};
	const onTextChange = (color: string) => {
		setTextColor(color);
		setTextUnset(false);
	};

	return (
		<>
			<Field label="Background color" aside={bgUnset ? '(none)' : undefined}>
				<ColorInput color={bgColor} onColorChange={onBgChange} inactive={bgUnset} />
			</Field>
			<Field label="Text color" aside={textUnset ? '(none)' : undefined}>
				<ColorInput color={textColor} onColorChange={onTextChange} inactive={textUnset} />
			</Field>
		</>
	);
};

export default ColorEditControl;
